import AwoodsWidget from './components/Awoods.vue'
import type CommentingModel from './data/CommentingModel'
import MissingNecessaryFieldError from './exceptions/MissingNecessaryFieldError'
import type { AwoodsOptions, LanguageOptions } from './data/AwoodsOptions'
import { checkNecessaryOptions, useDefault } from './utils/Utils'
import SmilieManager from './smilie/SmilieManager'
import type RecentComment from './data/RecentComment'
import type ServiceProvider from './service-provider/ServiceProvider'
import Waline from './service-provider/Waline'
import DomActions from './dom/DomActions'
import Compatibility from './utils/Compatibility'
import SerializableStorage from './storage/SerializableStorage'
import StateData from './storage/StateInfo'
import { type App, type ComponentPublicInstance, createApp } from 'vue'
import moment from 'moment'
import SmilieGenerateTool from './smilie/SmilieGenerateTool'
import $ from 'jquery'
import pkg from '../package.json';
moment.locale('zh-cn')

var defaultOptions: AwoodsOptions = {
    el: undefined as any,
    api: undefined as any,
    pathname: location.pathname,
    manual_mode: false,
    visit_interval: 30*60,
    smilies: [],
    smilie_as_url: false,
    avatar_default: '',
    avatar_source: 'https://www.gravatar.com/avatar/',
    avatar_nocache: false,
    avatar_force: false,
    login_to_comment: false,
    capacity: 10,
    focus: true,
    paginator_width: 3,
    dual_paginator: true,
    mail_required: true,
    website_required: true,
    standalone_login_button: true,
    language: {
        author: '作者',
        comment_tips: '有什么想要补充的吗',
        nick: '昵称',
        mail: '邮箱',
        website: '网站',
        no_comment: '这里什么也没有',
    } as LanguageOptions,
}

export default class Awoods
{
    opt: AwoodsOptions
    lang: LanguageOptions // shortcut for this.opt.language
    smilieManager: SmilieManager
    serviceProvider: ServiceProvider
    domActions: DomActions

    mainComponent: any = null // AwoodsWidget
    mainApp: App<Element>|null = null
    editorComponent: any = null // CommentEditor
    profileComponent: any = null // Profile

    stateData = new SerializableStorage(StateData, 'awoodscomment-state-data')

    constructor(options: AwoodsOptions)
    {
        if (!options)
            throw new MissingNecessaryFieldError('Options-Object')
        
        options = Compatibility(options)
        
        checkNecessaryOptions(['el', 'api'], options)

	    this.opt = useDefault(options, defaultOptions)
        this.lang = this.opt.language as LanguageOptions

        this.stateData.read()

        this.smilieManager = new SmilieManager()
        this.domActions = new DomActions(this)
        this.serviceProvider = new Waline(this)

        if(!this.opt.manual_mode)
        {
            if (document.querySelector('#'+this.opt.el) != null)
                this.mount()
            else
                console.error('can not find the element by id: ' + '#'+this.opt.el)
        }
    }

    setOptions(optionsOverrode?: AwoodsOptions)
    {
        if(typeof(optionsOverrode) == 'object')
            this.opt = useDefault(optionsOverrode, this.opt)
    }

    /**
     * 创建评论系统的DOM
     */
    async mount()
    {
        let domExists = document.getElementById(this.opt.el) != null
        
        this.domActions.renderCommentCount()
        this.domActions.renderPageViews()

        if (domExists)
        {
            this.mainApp = createApp(AwoodsWidget, { owner: this })
            this.mainComponent = this.mainApp.mount('#'+this.opt.el)
            
            this.editorComponent = this.mainComponent.$refs.editor as App
            this.profileComponent = this.editorComponent.$refs.profile as App
    
            this.domActions.recordVisit(false)
            
            await this.refresh()
    
            this.smilieManager.load(this.opt.smilies).then(() => {
                let temp = this.mainComponent.allComments
                this.mainComponent.allComments = []
                // this.mainComponent.$forceUpdate()
                setTimeout(() => {
                    this.mainComponent.allComments = temp
                }, 10);
            })
    
            if(this.opt.focus)
                this.focus()
        } else {
            this.domActions.recordVisit(true)
        }
    }

    /**
     * 聚焦到目标评论上（仅当从通知邮件内的链接跳转过来时）
     */
    focus()
    {
        let hash = location.hash
        try {
            let commentDom = $(hash)
            if(commentDom.length > 0)
            {
                $('html, body').animate({
                    scrollTop: commentDom?.offset()?.top ?? 0
                }, 1000)
            }
        } catch(e) {
            console.warn('Comment could not be focused on: '+hash)
        }
        
    }

    /**
     * 销毁dom
     */
    destroy()
    {
        if(this.mainComponent != null)
        {
            this.mainApp?.unmount()
            this.editorComponent = null
            this.mainComponent = null
        }
    }

    /** 重新加载评论列表(仅SPA适用) */
    update(optionsOverrode?: AwoodsOptions)
    {
        if(!this.isDestroyed())
            this.destroy()
        this.setOptions(optionsOverrode)
        this.mount()
    }

    /**
     * 检查dom是否已经被销毁了
     */
    isDestroyed()
    {
        return this.mainComponent == null
    }

    /** 
     * 刷新评论数据
     */
    async refresh()
    {
        return this.serviceProvider.refresh()
    }

    /** 
     * 提交评论
     */
    async submit(comment: CommentingModel)
    {
        return this.serviceProvider.submit(comment)
    }

    /** 
     * 获取最近评论
     */
    async recent(count=5): Promise<RecentComment[]>
    {
        return this.serviceProvider.recent(count)
    }

    /** 
     * 获取评论数量
     */
    async count(pathname: string): Promise<number>
    {
        return this.serviceProvider.count(pathname)
    }

    /** 
     * 获取浏览次数
     */
    async views(pathname: string): Promise<number>
    {
        return this.serviceProvider.views(pathname)
    }

    /** 
     * 给某个页面的阅读次数+1
     */
    async visit(pathname: string): Promise<number>
    {
        return this.serviceProvider.visit(pathname)
    }

    static version()
    {
        return pkg.version
    }

    static isProduction()
    {
        return (import.meta as any).env.PROD === 'production'
    }

    /** 启动表情meta生成工具 */
    static smilieTool()
    {
        SmilieGenerateTool.generateFromLocalDisk()
    }
}

// 暴露到全局变量
(window as any).Awoods = Awoods;